import { getFragmentQueryDocument } from '@apollo/client/utilities';
import {
  DocumentNode,
  FragmentDefinitionNode,
  GraphQLError,
  Kind,
  concatAST,
  NoFragmentCyclesRule as noFragmentCyclesRule,
  print,
  separateOperations,
  visit,
} from 'graphql';
import { ASTValidationContext } from 'graphql/validation/ValidationContext';

export const composeDocument = (
  document: DocumentNode,
  fragmentDocuments: readonly DocumentNode[] = [],
): DocumentNode => {
  const fragments = new Map<string, FragmentDefinitionNode>();

  for (const source of fragmentDocuments) {
    if (
      source.definitions.some(
        definition => definition.kind !== Kind.FRAGMENT_DEFINITION,
      )
    ) {
      throw new Error(
        'Fragment documents must contain only fragment definitions',
      );
    }
  }

  const mergedDocument = concatAST([document, ...fragmentDocuments]);
  for (const definition of mergedDocument.definitions) {
    if (definition.kind !== Kind.FRAGMENT_DEFINITION) continue;
    const name = definition.name.value;
    const existing = fragments.get(name);
    if (existing && print(existing) !== print(definition)) {
      throw new Error(`Conflicting fragment definitions for "${name}"`);
    }
    fragments.set(name, definition);
  }

  const fragmentDocument: DocumentNode = {
    kind: Kind.DOCUMENT,
    definitions: [...fragments.values()],
  };
  const operations = document.definitions.filter(
    definition => definition.kind === Kind.OPERATION_DEFINITION,
  );
  const operationDocuments = operations.length
    ? operations.map(operation => ({
        ...fragmentDocument,
        definitions: [operation, ...fragmentDocument.definitions],
      }))
    : document.definitions.flatMap(definition =>
        definition.kind === Kind.FRAGMENT_DEFINITION
          ? [getFragmentQueryDocument(fragmentDocument, definition.name.value)]
          : [],
      );
  const reachableDefinitions = new Set(
    operationDocuments.flatMap(operationDocument =>
      Object.values(separateOperations(operationDocument)).flatMap(
        operation => operation.definitions,
      ),
    ),
  );
  const composedDocument: DocumentNode = {
    ...document,
    definitions: [
      ...document.definitions.filter(
        definition => definition.kind !== Kind.FRAGMENT_DEFINITION,
      ),
      ...[...fragments.values()].filter(fragment =>
        reachableDefinitions.has(fragment),
      ),
    ],
  };
  const context = new ASTValidationContext(composedDocument, error => {
    throw error;
  });
  visit(composedDocument, {
    FragmentSpread(node) {
      if (!context.getFragment(node.name.value)) {
        throw new GraphQLError(`Unknown fragment "${node.name.value}".`, {
          nodes: node,
        });
      }
    },
  });
  visit(composedDocument, noFragmentCyclesRule(context));

  return composedDocument;
};

export const replaceFragment = (
  document: DocumentNode,
  oldFragmentName: string,
  newFragment: DocumentNode,
  newFragmentName: string,
): DocumentNode => {
  if (
    !newFragment.definitions.some(
      definition =>
        definition.kind === Kind.FRAGMENT_DEFINITION &&
        definition.name.value === newFragmentName,
    )
  ) {
    throw new Error(`Replacement fragment "${newFragmentName}" is not defined`);
  }

  const definitions = document.definitions
    .filter(
      definition =>
        definition.kind !== Kind.FRAGMENT_DEFINITION ||
        definition.name.value !== oldFragmentName,
    )
    .map(definition =>
      visit(definition, {
        FragmentSpread(node) {
          if (node.name.value !== oldFragmentName) return undefined;
          return {
            ...node,
            name: { ...node.name, value: newFragmentName },
          };
        },
      }),
    );

  return composeDocument({ ...document, definitions }, [newFragment]);
};
